import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * WHETHER THE APP IS IN THE FOREGROUND, AND WHEN THAT CHANGES.
 *
 * A phone is not a browser tab. An app sits backgrounded for days and then comes back to a
 * screen that has been showing week-old state the whole time, having never re-asked
 * anything — the version floor, the session, a list. A tab gets closed; an app just waits.
 * This is the signal that says "you are being looked at again, go and check".
 *
 * Both directions are reported. Pausing a poll on the way out is as real a need as
 * refetching on the way back.
 *
 * WHY `pause`/`resume` AND NOT `appStateChange`. The obvious listener is the wrong one.
 * `appStateChange` is driven by iOS `didBecomeActive`/`willResignActive`, which fire when
 * the app merely stops being FRONTMOST: a permission dialog, the app switcher peeked at and
 * dismissed, Control Centre pulled down, an incoming call banner. None of those is "the user
 * went away and came back", and each one produces a full false pair. `pause`/`resume` come
 * from `didEnterBackground`/`willEnterForeground` instead — the app was genuinely put away.
 * That is also what the web's `visibilitychange` means, so the two halves of this seam
 * finally measure the same thing, which is the entire point of a seam.
 *
 * NO DEBOUNCE LIVES HERE, ON PURPOSE. Choosing the quiet event removes most of the reason
 * anyone would want one, but a user who flicks between two apps still generates a pair a
 * second. Anything expensive hung off this — a network round trip, a token refresh — debounces
 * AT THE CONSUMER, which is the only place that knows how expensive its own work is and how
 * stale is too stale. A delay baked in here would be the wrong length for every caller and
 * invisible to all of them.
 *
 * Repeats are collapsed: two `true` in a row report once. Both platforms alternate today, so
 * this is a guard rather than a fix — it is what makes "one round trip, one pair" a property
 * of this module instead of a promise about someone else's event ordering. It is not a
 * debounce: nothing is delayed and no genuine transition is dropped. If a second native
 * listener is ever added here, check this first — a second `true` arriving behind the first
 * would be swallowed.
 *
 * TWO THINGS IT DELIBERATELY DOES NOT DO, both of which look like omissions:
 *
 *   - No `getForegroundState()`, though `network.ts` next door pairs its listener with a
 *     getter. `App.getState()` answers the `appStateChange` question ("frontmost?"), not this
 *     one ("backgrounded?"), so a getter here would quietly disagree with the listener beside
 *     it. Nothing needs a one-shot read yet; when something does, it needs a straight answer.
 *   - No elapsed time in the payload. A consumer that cares whether it was away for 200ms or
 *     three days has to note the time itself. Worth revisiting the first time two consumers
 *     write that clock.
 *
 * AND ONE OVERLAP TO KNOW ABOUT: TanStack Query is already configured with
 * `refetchOnWindowFocus` in app/query/query-client.ts, and its focus manager watches the same
 * `visibilitychange`. Server data in the cache is therefore ALREADY revalidated on return.
 * Do not hang a second refetch off this signal without deciding which of the two owns it.
 */
export type ForegroundState = { foreground: boolean };

export function onForegroundChange(handler: (state: ForegroundState) => void): () => void {
  let last: boolean | undefined;
  const report = (foreground: boolean) => {
    if (foreground === last) return;
    last = foreground;
    handler({ foreground });
  };

  if (!Capacitor.isNativePlatform()) {
    const onVisibilityChange = () => report(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }

  const paused = App.addListener("pause", () => report(false));
  const resumed = App.addListener("resume", () => report(true));
  return () => {
    void paused.then((h) => h.remove());
    void resumed.then((h) => h.remove());
  };
}
